import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import ControlCameraIcon from "@mui/icons-material/ControlCamera";
import CircleIcon from "@mui/icons-material/Circle";
import CropIcon from "@mui/icons-material/Crop";
import WbIncandescentIcon from "@mui/icons-material/WbIncandescent";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import AdjustIcon from "@mui/icons-material/Adjust";
import EmailIcon from "@mui/icons-material/Email";
import FlipCard from "./FlipCard";
import LineBreak from "./LineBreak";
import researchbg from "../assets/research.jpg";
import researchpic from "../assets/research/research.jpg";
import evaluationpic from "../assets/research/evaluation.jpg";
import pocpic from "../assets/research/poc.jpg";
import advisorypic from "../assets/research/advisory.jpg";

// Lists and structured content are loaded from i18n (text) while images remain in-code

export default function ResearchSection(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const positionItems =
    (t("research.positionItems", { returnObjects: true }) as string[]) || [];
  const engagementModelsItems =
    (t("research.engagementModelsItems", {
      returnObjects: true,
    }) as string[]) || [];
  const strengthsItems =
    (t("research.strengthsItems", { returnObjects: true }) as string[]) || [];
  const noteItems =
    (t("research.noteItems", { returnObjects: true }) as string[]) || [];
  const areaSupport =
    (t("research.areaSupport", { returnObjects: true }) as any[]) || [];
  const areaImages = [researchpic, evaluationpic, pocpic, advisorypic];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${researchbg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 75%",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="research"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary" gutterBottom>
              {t("research.title")}
            </Typography>
          </Box>
          <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="research.intro"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <ControlCameraIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("research.positionTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("research.positionStart")}
            </Typography>
            <List dense>
              {positionItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("research.positionEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ my: 2 }}
            >
              <CropIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("research.areaTitle")}
            </Typography>
            <Grid container spacing={4} sx={{ justifyContent: "center" }}>
              {areaSupport.map((item, i) => (
                <Grid size={{ xs: 12, md: 6 }} key={item.title}>
                  <FlipCard
                    title={item.title}
                    image={areaImages[i]}
                    description={item.description}
                  />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <WbIncandescentIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("research.engagementTitle")}
            </Typography>
            <List dense>
              {engagementModelsItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("research.engagementEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <WorkspacePremiumIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("research.strengthsTitle")}
            </Typography>
            <List dense>
              {strengthsItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <QuestionAnswerIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("research.inquiryTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("research.inquiryContent")}
            </Typography>
            {i18n.language === "en" && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="h5"
                  color="text.primary"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  <SpeakerNotesIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                  {t("research.noteTitle")}
                </Typography>
                <List dense>
                  {noteItems.map((item) => (
                    <ListItem key={item}>
                      <ListItemIcon
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "warning.main",
                          },
                        }}
                      >
                        <AdjustIcon sx={{ mr: 1 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        sx={{ color: "text.primary" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            <Box
              sx={{
                mt: 3,
                mx: "auto",
                width: { xs: "95%", md: "75%" },
                textAlign: "center",
              }}
            >
              <Button
                component="a"
                href="mailto:sharkshooter2011@gmail.com"
                endIcon={<EmailIcon />}
                size="large"
                variant="contained"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 999,
                  textTransform: "none",
                  boxShadow: 3,
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 6,
                  },
                }}
              >
                {t("research.contactBtnTitle")}
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
